import { t } from '@lingui/core/macro';
import { Box, IconButtonProps, styled } from '@mui/material';
import { ArrowUpIcon, AtSignIcon, FileTextIcon, PaperclipIcon, XIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { usePersistedState } from '../../hooks/usePersistedState';
import { colorSystem } from '../../theme';
import { AvatarIcon } from '../AvatarIcon';
import { PrimaryIconButton, SecondaryIconButton, TertiaryIconButton } from '../buttons';
import { CircularProgress } from '../CircularProgress';
import { Flexbox } from '../Flexbox';
import { iconSlot } from '../icons/iconSlot';
import { Tooltip } from '../Tooltip';
import { CommentItem } from './components/CommentItem';
import { CommentsInput } from './components/CommentsInput';
import { Comment, CommentContent, MentionComponent } from './types';
import { ATTACHMENT_PREFIX } from './utils/parseCommentString';

const MAX_INPUT_LENGTH = 600 - 10;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

function isImageFile(file: File): boolean {
    return IMAGE_TYPES.includes(file.type);
}

const noop = () => {};

interface User {
    id: string;
}
export interface CommentsProps<TUser extends User> {
    /**
     * A unique key that will be used to persist and load the state of the
     * input so that users don't lose "drafts"
     */
    storageKey: string;
    comments: Comment[];
    onSubmitNewComment?: (comment: CommentContent) => void;
    onEdit?: (comment: Comment) => void;
    onDelete?: (comment: Comment) => void;
    users: TUser[];
    currentUser: TUser;
    formatMention: (user: TUser) => string;
    formatUser: (user: TUser) => { firstName: string; lastName: string; email: string };
    formatOrg: (user: TUser) => string;
    overrides?: {
        Container?: React.ComponentType;
        Mention?: React.ComponentType<{ id: string; label: string }>;
    };
    disabled?: boolean;
    placeholder?: string;
    /**
     * Optional callback to upload a file. If provided, an attachment button will be shown.
     * The callback should upload the file and return the file path.
     */
    onUploadFile?: (file: File) => Promise<string>;
    /**
     * Optional callback to get the download URL for an attachment.
     * Required if onUploadFile is provided to display attachments in comments.
     */
    getAttachmentUrl?: (filePath: string) => Promise<string>;
}

interface PendingFile {
    file: File;
    previewUrl: string;
}

export function Comments<TUser extends User>(props: CommentsProps<TUser>): JSX.Element {
    const {
        currentUser,
        comments,
        onSubmitNewComment = noop,
        onEdit = noop,
        onDelete = noop,
        users,
        formatMention,
        formatUser,
        formatOrg,
        storageKey,
        placeholder,
        disabled = false,
        overrides = {},
        onUploadFile,
        getAttachmentUrl,
    } = props;
    const { Container = DefaultContainer, Mention = DefaultMention } = overrides;
    const [value, onChange] = usePersistedState<string>(storageKey, '', sessionStorage);
    const [pendingFiles, setPendingFiles] = React.useState<PendingFile[]>([]);
    const [isUploading, setIsUploading] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmitComment = React.useCallback(async () => {
        if (isUploading) return;

        let finalContent = value.trim();

        // Upload pending files and append attachment syntax
        if (pendingFiles.length > 0 && onUploadFile) {
            setIsUploading(true);
            try {
                const uploadedPaths = await Promise.all(pendingFiles.map((pf) => onUploadFile(pf.file)));

                // Append attachment markers to the comment text
                const attachmentMarkers = uploadedPaths.map((path) => `![](${ATTACHMENT_PREFIX}:${path})`).join('');
                finalContent = finalContent + attachmentMarkers;

                // Clean up preview URLs for images
                pendingFiles.forEach((pf) => {
                    if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
                });
                setPendingFiles([]);
            } catch {
                // If upload fails, don't submit the comment
                setIsUploading(false);
                return;
            }
            setIsUploading(false);
        }

        onSubmitNewComment({ type: 'string.v1', content: finalContent });
        onChange('');
    }, [onChange, value, onSubmitNewComment, pendingFiles, onUploadFile, isUploading]);

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    const handleKeyUp: React.KeyboardEventHandler = React.useCallback(
        (event) => {
            if (event.key === 'Enter' && event.ctrlKey && isValid(value, pendingFiles)) {
                handleSubmitComment();
            }
        },
        [handleSubmitComment, value, pendingFiles],
    );

    const handleReply = React.useCallback(
        (comment: Comment) => {
            onChange(
                comment.content.content
                    .split('\n')
                    .map((line) => '>' + line)
                    .join('\n') + '\n\n',
            );
            inputRef.current?.focus();
        },
        [onChange],
    );

    const handleFileSelect = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        const newPendingFiles: PendingFile[] = Array.from(files).map((file) => ({
            file,
            // Only create preview URLs for images
            previewUrl: isImageFile(file) ? URL.createObjectURL(file) : '',
        }));

        setPendingFiles((prev) => [...prev, ...newPendingFiles]);

        // Reset the input so the same file can be selected again
        event.target.value = '';
    }, []);

    const handlePasteFiles = React.useCallback((files: File[]) => {
        const newPendingFiles: PendingFile[] = files.map((file) => ({
            file,
            previewUrl: isImageFile(file) ? URL.createObjectURL(file) : '',
        }));

        setPendingFiles((prev) => [...prev, ...newPendingFiles]);
    }, []);

    const removePendingFile = React.useCallback((index: number) => {
        setPendingFiles((prev) => {
            const removed = prev[index];
            if (removed?.previewUrl) {
                URL.revokeObjectURL(removed.previewUrl);
            }
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    // Key the auto-scroll on the actual comment set instead of the array
    // identity, which changes on every parent re-render and would otherwise
    // yank a reader who scrolled up back to the bottom.
    const lastCommentId = comments.length > 0 ? comments[comments.length - 1].id : undefined;
    React.useEffect(() => {
        const element = scrollContainerRef.current;
        if (!element) {
            return;
        }
        // Hack: The Storybook snapshot tests don't have a scrollTo method
        'scrollTo' in element && element.scrollTo(0, element.scrollHeight);
    }, [comments.length, lastCommentId]);

    // Clean up preview URLs on unmount
    React.useEffect(() => {
        return () => {
            pendingFiles.forEach((pf) => {
                if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
            });
        };
    }, [pendingFiles]);

    return (
        <Container>
            {comments.length > 0 ? (
                <CommentsContainer ref={scrollContainerRef}>
                    {comments.map((comment) => {
                        return (
                            <CommentItem
                                overrides={{ MentionComponent: Mention }}
                                key={comment.id}
                                comment={comment}
                                onReply={handleReply}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                disabled={disabled}
                                getAttachmentUrl={getAttachmentUrl}
                            />
                        );
                    })}
                </CommentsContainer>
            ) : null}
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', flexShrink: 0 }}
                className="input-container"
                style={{
                    boxShadow: comments.length === 0 && pendingFiles.length === 0 ? 'none' : undefined,
                    background: getInputContainerBackgroundColor(value),
                }}
            >
                <AvatarIcon size="small" user={formatUser(currentUser)} />
                <CommentsInput
                    inputRef={inputRef}
                    placeholder={placeholder || t`Add a comment...`}
                    value={value}
                    onChange={onChange}
                    formatMention={formatMention}
                    formatOrg={formatOrg}
                    users={users}
                    disabled={disabled || isUploading}
                    onKeyUp={handleKeyUp}
                    onPasteFiles={onUploadFile ? handlePasteFiles : undefined}
                />
                {onUploadFile && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                        <ButtonAttach
                            disabled={disabled || isUploading}
                            onClick={() => fileInputRef.current?.click()}
                        />
                    </>
                )}
                <ButtonMention
                    style={{ visibility: users.length === 0 ? 'hidden' : 'visible' }}
                    disabled={disabled || isUploading}
                    onClick={() => {
                        inputRef.current?.focus();
                        onChange(value + ' @');
                    }}
                />
                <ButtonSend
                    disabled={!isValid(value, pendingFiles) || disabled || isUploading}
                    onClick={handleSubmitComment}
                    isLoading={isUploading}
                />
            </Box>
            {pendingFiles.length > 0 && (
                <PendingFilesContainer>
                    {pendingFiles.map((pf, index) => (
                        <PendingFilePreview key={`${pf.file.name}-${index}`}>
                            {pf.previewUrl ? (
                                <img src={pf.previewUrl} alt={pf.file.name} />
                            ) : (
                                <Tooltip title={pf.file.name}>
                                    <FileIconContainer>
                                        <FileTextIcon size={24} color={colorSystem.neutral[5]} />
                                    </FileIconContainer>
                                </Tooltip>
                            )}
                            <RemoveFileButton
                                aria-label={removeFileLabel(pf.file.name)}
                                onClick={() => removePendingFile(index)}
                                disabled={isUploading}
                            >
                                <XIcon />
                            </RemoveFileButton>
                        </PendingFilePreview>
                    ))}
                </PendingFilesContainer>
            )}
        </Container>
    );
}

/** Names the file, so several pending attachments' remove buttons can be told apart. */
function removeFileLabel(fileName: string): string {
    return t`Remove file: ${fileName}`;
}

function isValid(comment: string, pendingFiles: PendingFile[]) {
    const hasContent = comment.trim().length > 0 || pendingFiles.length > 0;
    return hasContent && comment.length < MAX_INPUT_LENGTH;
}

function getInputContainerBackgroundColor(comment: string): string | undefined {
    if (comment.length > MAX_INPUT_LENGTH) {
        return colorSystem.red[3];
    }
    if (comment.length > MAX_INPUT_LENGTH - 50) {
        return colorSystem.yellow[2];
    }
    return undefined;
}

const DefaultContainer = styled(Flexbox)({
    flexDirection: 'column',
    border: `1px solid ${colorSystem.neutral[2]}`,
    borderRadius: 4,
});

const DefaultMention: MentionComponent = (props) => {
    return (
        <span
            style={{
                background: colorSystem.primary[1],
                color: colorSystem.primary[8],
                borderRadius: 4,
                padding: 2,
            }}
        >
            {props.label}
        </span>
    );
};

const ButtonSend = ({ isLoading, ...props }: IconButtonProps & { isLoading?: boolean }) => {
    return (
        <Tooltip describeChild title={t`Send (Ctrl + Enter)`}>
            <PrimaryIconButton
                size="medium"
                style={{ borderRadius: '100%', width: 20, height: 20 }}
                {...props}
                aria-label={t`Send`}
            >
                {isLoading ? <CircularProgress size="small" style={{ color: 'white' }} /> : <ArrowUpIcon />}
            </PrimaryIconButton>
        </Tooltip>
    );
};

const ButtonMention = (props: IconButtonProps) => {
    return (
        <Tooltip title={t`Mention a person`}>
            <TertiaryIconButton
                size="medium"
                style={{ borderRadius: '100%', width: 22, height: 22 }}
                {...props}
                aria-label={t`Mention a person`}
            >
                <AtSignIcon />
            </TertiaryIconButton>
        </Tooltip>
    );
};

const ButtonAttach = (props: IconButtonProps) => {
    return (
        <Tooltip title={t`Attach file`}>
            <TertiaryIconButton
                size="medium"
                style={{ borderRadius: '100%', width: 22, height: 22 }}
                {...props}
                aria-label={t`Attach file`}
            >
                <PaperclipIcon />
            </TertiaryIconButton>
        </Tooltip>
    );
};

const CommentsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 8,
    alignItems: 'flex-start',
    paddingRight: 12,
    // Allows the comment list to shrink and scroll when the surrounding
    // container constrains its height, keeping the input visible and making
    // the scroll-to-newest-comment effect work.
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
});

const PendingFilesContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: '0 8px 8px 8px',
    flexShrink: 0,
});

const PendingFilePreview = styled('div')({
    position: 'relative',
    width: 48,
    height: 48,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${colorSystem.neutral[3]}`,
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
});

// Smaller than any icon button size: the large slot would draw the X at 18px and overflow the circle.
const RemoveFileButton = styled(SecondaryIconButton)({
    position: 'absolute',
    top: 2,
    right: 2,
    width: 16,
    height: 16,
    minWidth: 16,
    padding: 0,
    backgroundColor: colorSystem.neutral[1],
    '&:hover': {
        backgroundColor: colorSystem.neutral[2],
    },
    ...iconSlot(12),
});

const FileIconContainer = styled('div')({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorSystem.neutral[1],
});
